/** Accessible modal shell shared by management, designer and submission screens. */
import { useEffect, useId, useRef, type PropsWithChildren, type ReactNode } from 'react';
import { useUiLocale } from '../app/ui-locale';
import { publicCopy } from '../features/renderer/public-copy';

interface ModalProps extends PropsWithChildren {
  open: boolean;
  title: string;
  onClose: () => void;
  footer?: ReactNode;
  wide?: boolean;
}

const focusableSelector = 'button:not([disabled]),a[href],input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

export function Modal({ open, title, onClose, footer, wide, children }: ModalProps) {
  const { locale } = useUiLocale();
  const copy = publicCopy(locale);
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    window.requestAnimationFrame(() => {
      dialogRef.current?.querySelector<HTMLElement>(focusableSelector)?.focus();
    });

    const listener = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== 'Tab' || !dialogRef.current) return;
      const focusable = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(focusableSelector));
      if (!focusable.length) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', listener);
    return () => {
      document.removeEventListener('keydown', listener);
      document.body.style.overflow = previousOverflow;
      previouslyFocused?.focus();
    };
  }, [open, onClose]);

  if (!open) return null;
  return <div className="df-modal-backdrop" role="presentation" onMouseDown={onClose}>
    <div ref={dialogRef} className={`df-modal ${wide ? 'df-modal--wide' : ''}`} role="dialog" aria-modal="true" aria-labelledby={titleId} onMouseDown={(event) => event.stopPropagation()}>
      <div className="df-modal__header"><h3 id={titleId}>{title}</h3><button type="button" className="df-icon-btn" onClick={onClose} aria-label={copy.close}>×</button></div>
      <div className="df-modal__body">{children}</div>
      {footer ? <div className="df-modal__footer">{footer}</div> : null}
    </div>
  </div>;
}
